/**
 * RsvpDisplay Component
 * Displays RSVP status counts and breakdown for a session
 * MED-173: Session RSVP Tracking feature
 */

import { Box, Group, Text } from '@mantine/core';
import type { SessionRsvpSummary } from '../../../types';

interface RsvpDisplayProps {
  /** RSVP summary for the session */
  summary?: SessionRsvpSummary;
  /** Whether the session has been finalized */
  isFinalized?: boolean;
}

export function RsvpDisplay({ summary, isFinalized }: RsvpDisplayProps) {
  // Before finalized or no summary: don't render
  if (!isFinalized || !summary) {
    return null;
  }

  const { total, accepted, declined, pending, tentative } = summary;

  // Calculate awaiting (pending + tentative)
  const awaiting = pending + tentative;

  // Determine if all accepted
  const allAccepted = accepted === total && total > 0;

  // Main count color
  const countColor = allAccepted ? 'green' : undefined;

  return (
    <Box style={{ textAlign: 'center' }}>
      <Group gap={4} justify="center" wrap="nowrap">
        <Text size="sm" fw={600} c={countColor}>
          {accepted}/{total}
        </Text>
        {awaiting > 0 && (
          <Text size="10px" c="orange" fw={500}>
            {awaiting} awaiting
          </Text>
        )}
        {declined > 0 && (
          <Text size="10px" c="red" fw={500}>
            {declined} declined
          </Text>
        )}
      </Group>
      <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>RSVPs</Text>
    </Box>
  );
}

export default RsvpDisplay;
